import * as _ from 'lodash';
import { useMemo, type VoidFunctionComponent } from 'react';

import { window } from '../../globals';
import useCallToAction from '../CallToAction/useCallToAction';
import Subnav from '../generic/Subnav';
import type { RouterProps } from '../../types';

import * as permissions from './shared/permissions';

type Props = {
  pathname: RouterProps['location']['pathname'];
};

type SettingsSubnavItem = {
  key: string;
  label: string;
  to: string;
};

// This hook is used to get the subnav items for the settings page
const useSubnavItems = () => useMemo(() => _.compact([
  {
    key: 'account',
    label: 'Account',
    to: '/account/settings',
  },
  {
    key: 'organization',
    label: 'Organization',
    //  Users who don't have permission to view the general settings page should be redirected to the teams page,
    //  because they are always able to view the teams page.
    to: permissions.shouldDisplayOrganizationGeneralSettings()
      ? '/account/settings/organization'
      : '/account/settings/organization/teams',
  },
  permissions.shouldDisplayProjectSettings() && {
    key: 'projects',
    label: 'Projects',
    to: '/account/settings/projects',
  },
  {
    key: 'integrations',
    label: 'Integrations',
    to: '/account/settings/integrations',
  },
  permissions.shouldDisplayLanguageSettings() && {
    key: 'languages',
    label: 'Languages',
    to: '/account/settings/languages',
  },
  !window.on_prem && permissions.shouldDisplayBillingSettings() && {
    key: 'subscription',
    label: 'Subscription',
    to: '/account/settings/billing',
  },
  permissions.shouldDisplayAuditLogs() && {
    key: 'audit-logs',
    label: 'Audit Logs',
    to: '/account/settings/audit-logs',
  },
// eslint-disable-next-line react-hooks/exhaustive-deps -- hasPermissions is iterating through user.permissions, which is why we include this here, even though it won't likely change.
]), [window.user?.permissions]);

// This hook is used to get the subnav items and the active item for the settings page.
const useSubnav = (pathname: string): [SettingsSubnavItem[], SettingsSubnavItem] => {
  const subnavItems = useSubnavItems();
  //  Because all of the routes start with "/account/settings", we need to handle the special case of the account settings page.
  if (pathname === '/account/settings') {
    return [subnavItems, subnavItems[0] as SettingsSubnavItem];
  }
  const activeItem = [...subnavItems]
    .slice(1) // Remove the /account/settings item from the list
    .find((item) => pathname.includes(item.to));
  return [subnavItems, activeItem as SettingsSubnavItem];
};

/**
 * SettingsNavigation renders the top-level Subnav for all the settings pages, including:
 *   - Account/Login
 *   - Organization
 *   - Projects
 *   - Integrations
 *   - Languages
 *   - Subscription
 *   - Audit Logs
 */
const SettingsNavigation: VoidFunctionComponent<Props> = ({ pathname }) => {
  const [subnavItems, activeItem] = useSubnav(pathname);

  //  This is used to ensure that the old-style NavState from Redux will
  //  clear any existing "nav" or call-to-action state.
  useCallToAction();

  return (
    <Subnav activeKey={activeItem?.key} variant="small">
      {subnavItems.map((item) => (
        <Subnav.Item
          key={item.key}
          to={item.to}
          navKey={item.key}
        >
          {item.label}
        </Subnav.Item>
      ))}
    </Subnav>
  );
};

export default SettingsNavigation;
